/**
 * Support chat on a location's pages in the app (/app/l/:locationId/...,
 * WO-69, Anand, 13 Oct 26): the launcher shows, and support sees the
 * location and its workspace by name, as the account's overview names them
 * (the read AppLocationShell's access check makes anyway, held in appData).
 *
 * A location the overview does not list (an ops executive's, Fly staff's, or
 * one past the 500 the overview reads) is still named by its id; its name and
 * workspace are sent as unknown rather than guessed (src/lib/appSupportChat.ts).
 */
import { useEffect, useState } from 'react';
import { type AppChatContext, useAppSupportChat } from '@/lib/appSupportChat';
import { getCachedOverview, knownSessionUser, subscribeOverview } from '../appData';
import type { AppOverview } from '../appModel';

/** The location on screen and its workspace, from the account's overview. Pure. */
export function locationChatContext(overview: AppOverview | null | undefined, locationId: string): AppChatContext {
  const userName = overview?.user?.name?.trim() || null;
  for (const ws of overview?.workspaces ?? []) {
    const loc = ws.locations.find((l) => l.location_id === locationId);
    if (loc) return { userName, workspace: { id: ws.workspace_id, name: ws.name }, location: { id: locationId, name: loc.name } };
  }
  return { userName, workspace: null, location: { id: locationId, name: null } };
}

export function useLocationSupportChat(locationId: string): void {
  const [overview, setOverview] = useState<AppOverview | null>(() => getCachedOverview(knownSessionUser()));
  // The shell's access check reads the overview; its answer (and any later one) names the location here.
  useEffect(() => {
    setOverview(getCachedOverview(knownSessionUser()));
    return subscribeOverview(() => setOverview(getCachedOverview(knownSessionUser())));
  }, []);
  useAppSupportChat(locationChatContext(overview, locationId));
}
